import {
  PresentationDraft,
  PresentationRepository,
} from '../session/PresentationSession';
import { AvailabilityReport } from '../session/PresentationAvailability';
import {
  CloudPresentationDetail,
  CloudPresentationSummary,
  PresentationApiClient,
  presentationApiClient,
} from './PresentationApiClient';

const CLOUD_PREFIX = 'cloud-';

export function cloudDraftId(storageId: number): string {
  return `${CLOUD_PREFIX}${storageId}`;
}

export function storageIdFromDraft(id: string): number {
  if (!id.startsWith(CLOUD_PREFIX)) throw new Error('Ungültige Cloud-Präsentations-ID.');
  const value = Number(id.slice(CLOUD_PREFIX.length));
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error('Ungültige Cloud-Präsentations-ID.');
  return value;
}

export function isCloudDraftId(id: string | null): boolean {
  return Boolean(id?.startsWith(CLOUD_PREFIX));
}

export function summaryToDraft(item: CloudPresentationSummary, content = ''): PresentationDraft {
  return {
    id: cloudDraftId(item.id),
    title: item.title,
    subtitle: item.description || 'Cloud-Präsentation',
    updatedAt: item.updated_at,
    publication: item.is_public ? 'published' : 'private',
    ownerTenantId: null,
    source: 'cloud',
    content,
  };
}

function detailToDraft(item: CloudPresentationDetail): PresentationDraft {
  return summaryToDraft(item, item.content);
}

export class CloudPresentationRepository implements PresentationRepository {
  constructor(private readonly api: PresentationApiClient = presentationApiClient) {}

  async list(): Promise<PresentationDraft[]> {
    return (await this.api.list()).map((item) => summaryToDraft(item));
  }

  async create(input: Partial<Pick<PresentationDraft, 'title' | 'subtitle'>> = {}): Promise<PresentationDraft> {
    throw new Error(`Cloud-Entwürfe entstehen aus einem lokalen Entwurf (${input.title || 'ohne Titel'}).`);
  }

  async get(id: string): Promise<PresentationDraft | null> {
    return detailToDraft(await this.api.get(storageIdFromDraft(id)));
  }

  async save(draft: PresentationDraft): Promise<PresentationDraft> {
    return detailToDraft(await this.api.update(storageIdFromDraft(draft.id), {
      title: draft.title,
      description: draft.subtitle,
      content: draft.content,
    }));
  }

  async saveLocalToCloud(draft: PresentationDraft): Promise<PresentationDraft> {
    return detailToDraft(await this.api.create({
      title: draft.title,
      description: draft.subtitle,
      content: draft.content,
    }));
  }

  async setPublication(draft: PresentationDraft, isPublic: boolean): Promise<PresentationDraft> {
    const summary = await this.api.setPublication(storageIdFromDraft(draft.id), isPublic);
    return summaryToDraft(summary, draft.content);
  }

  /** #1762: what an anonymous visitor would get for this draft right now. */
  async availability(draftId: string): Promise<AvailabilityReport> {
    const result = await this.api.getAvailability(storageIdFromDraft(draftId));
    return { isPublic: result.is_public, anonymous: result.anonymous, checkedAt: result.checked_at };
  }
}

export const cloudRepository = new CloudPresentationRepository();
